import React from 'react';
import { ShieldCheck } from 'lucide-react';

/**
 * Compact PO settings editor used inside Company / Property edit forms.
 * `value` is a `po_settings` object (may be undefined). `onChange` returns a
 * new object. At the property level, null on any field means "inherit from
 * company". At the company level, null means "use default (off)".
 */
const Toggle = ({ checked, onChange, label, hint, testId }) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <input
      type="checkbox"
      data-testid={testId}
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 accent-[#D4AF37]"
    />
    <span className="text-sm">
      <span className="text-white">{label}</span>
      {hint && <span className="text-[#525252] text-xs block">{hint}</span>}
    </span>
  </label>
);

const PoSettingsForm = ({ value = {}, onChange, scope = 'company' }) => {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const isProperty = scope === 'property';

  return (
    <div data-testid={`po-settings-${scope}`} className="bg-[#0A0A0A] border border-[#262626] rounded p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
        <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase">
          Purchase Order Settings {isProperty && '(Property Override)'}
        </p>
      </div>
      {isProperty && (
        <p className="text-[#525252] text-[11px]">
          Leave any toggle off to inherit the company default. Property-level settings override the company when set.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Toggle testId={`po-${scope}-required`}        checked={v.po_required}                onChange={(b) => set('po_required', b)}                label="PO Required"                  hint="A PO must be on file before service" />
        <Toggle testId={`po-${scope}-before-sched`}    checked={v.required_before_scheduling} onChange={(b) => set('required_before_scheduling', b)} label="Required Before Scheduling" hint="Block dispatch until PO is received" />
        <Toggle testId={`po-${scope}-before-inv`}      checked={v.required_before_invoice}    onChange={(b) => set('required_before_invoice', b)}    label="Required Before Invoice"    hint="Block invoice send until PO is received" />
        <Toggle testId={`po-${scope}-pm-enter`}        checked={v.allow_pm_to_enter !== false} onChange={(b) => set('allow_pm_to_enter', b)}          label="Allow PM to Enter PO"        hint="PMs may enter / edit PO on requests" />
        <Toggle testId={`po-${scope}-admin-enter`}     checked={v.allow_admin_to_enter !== false} onChange={(b) => set('allow_admin_to_enter', b)}     label="Allow NexFlow Admin to Enter PO" />
      </div>
      <div>
        <label className="text-white text-sm">Default PO Notes</label>
        <textarea
          data-testid={`po-${scope}-default-notes`}
          rows={2}
          value={v.default_po_notes || ''}
          onChange={(e) => set('default_po_notes', e.target.value)}
          className="w-full bg-[#141414] border border-[#262626] rounded p-2 text-white text-sm"
          placeholder="Standard notes appended to every PO on this account"
        />
      </div>
      <div>
        <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.18em] uppercase mb-1">Billing Contact (PO Issues)</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input data-testid={`po-${scope}-billing-name`}  placeholder="Name"  value={v.billing_contact_name  || ''} onChange={(e) => set('billing_contact_name', e.target.value)}  className="bg-[#141414] border border-[#262626] rounded h-9 px-3 text-white text-sm" />
          <input data-testid={`po-${scope}-billing-email`} placeholder="Email" value={v.billing_contact_email || ''} onChange={(e) => set('billing_contact_email', e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-9 px-3 text-white text-sm" />
          <input data-testid={`po-${scope}-billing-phone`} placeholder="Phone" value={v.billing_contact_phone || ''} onChange={(e) => set('billing_contact_phone', e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-9 px-3 text-white text-sm" />
        </div>
      </div>
    </div>
  );
};

export default PoSettingsForm;
